import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import UserProfileMenu from '../components/UserProfileMenu';
import { useAuth } from '../hooks/useAuth';
import Dh from '../components/Dh';
import { API_BASE_URL } from '@/utils/env';
import { useReportPricing } from '@/data/reportPricing';

// ── Palette ──────────────────────────────────────────────────────────────────
const BRAND   = '#1976d2';
const BRAND_BRT = '#5B9BD5';
const INK     = '#17263A';
const INK2    = '#42505F';
const MUTED   = '#6E7888';
const LINE    = '#E7EAEE';
const ICE     = '#E8F2FD';
const WHITE   = '#fff';
const BG      = '#F5F6F8';

// ── Scoring engine (faithful to Capital Readiness questionnaire v2.4) ──────────
const RUNWAY: Record<string, number> = {
  "24 months and over": 5, "18–24 months": 4, "12–18 months": 3, "6–12 months": 2, "under 6 months": 1,
};
const RUNCOL: Record<string, number> = {
  "under 6 months": 0, "6–12 months": 1, "12–18 months": 2, "18–24 months": 3, "24 months and over": 4,
};
function ratioPts(r: number): number {
  const t: [number, number][] = [[0.25,2],[0.5,3],[0.75,4],[1,4.5],[1.5,5],[2,4.5],[3,3.5],[5,2.5],[1e9,1.5]];
  for (const [hi, pts] of t) if (r <= hi) return pts;
  return 1.5;
}
function dilPts(r: number): { p: number; flag: boolean } {
  if (r > 1) return { p: 1, flag: true };
  const t: [number, number][] = [[0.05,3],[0.10,4],[0.25,5],[0.35,4.5],[0.50,3.5],[0.65,2.5],[0.80,1.5],[1.00,1]];
  for (const [hi, pts] of t) if (r <= hi) return { p: pts, flag: false };
  return { p: 1, flag: false };
}
const Q5M: Record<string, number[]> = {
  "Immediate: Breakeven now or within 1 month": [5,5,5,4,3],
  "Near: 1–3 months":                           [5,5,4,3,2],
  "Short: 3–6 months":                          [5,5,3,2,2],
  "Medium: 6–12 months":                        [4,4,2,2,1],
  "Long: 12–24+ months":                        [3,2,2,1,1],
  "Uncertain: No clear path to breakeven":      [1,1,1,1,1],
};
const Q6:  Record<string, number> = { "Hitting a revenue or paying-customer target":5,"Launching or completing the core product":4,"Signing key partnerships or contracts":4,"Closing this funding round":2,"Making a critical hire":2,"Just extending runway to survive":1 };
const Q8:  Record<string, number> = { "Convertible Notes":1,"Venture Debt":2,"Bank Loans":3,"Overdue Payables":4,"Other Liabilities":5 };
const Q9:  Record<string, number> = { "Recurring":5,"Mostly Stable":4,"Somewhat stable":3,"Irregular":2,"None":1 };
const Q10: Record<string, number> = { "Pivot strategy":5,"Cut costs":4,"Stay the course":3,"Seek funding":2 };
const Q11: Record<string, number> = { "More than 10 Years":5,"6 to 10 years":4,"1 to 5 years":3,"I don't have any experience":1 };
const Q13: Record<string, number> = { "Data-driven analysis":4,"Consult advisors":4,"Follow instinct":2,"Delay decisions":2 };
const Q14: Record<string, number> = { "Open to moderate risks":5,"Comfortable with big risks":3,"Prefer minimizing risks":1 };
const RAISE_MID: Record<string, number> = { "Under 100k":50000,"100k – 250k":175000,"250k – 500k":375000,"500k – 1M":750000,"1M – 2M":1500000,"2M – 5M":3500000,"5M+":7500000 };
const COST_MID:  Record<string, number> = { "Under 50k":25000,"50k – 100k":75000,"100k – 250k":175000,"250k – 500k":375000,"500k – 1M":750000,"1M – 2M":1500000,"2M+":3000000 };
const VAL_MID:   Record<string, number> = { "Under 500k":250000,"500k – 1M":750000,"1M – 3M":2000000,"3M – 5M":4000000,"5M – 10M":7500000,"10M – 20M":15000000,"20M+":30000000 };
const MULT = 1.515;

type Band = { name: string; max: number; color: string; desc: string };
const BANDS: Band[] = [
  { name:"Critical Risk",  max:22,       color:"#C0392B", desc:"Major foundational gaps; not yet fundable." },
  { name:"Very High Risk", max:27,       color:"#E0592A", desc:"Severe issues; extensive work needed." },
  { name:"High Risk",      max:32,       color:"#E08A1E", desc:"Key gaps exist; limited investor appeal." },
  { name:"Medium Risk",    max:37,       color:"#E6B400", desc:"Some readiness; improvements required." },
  { name:"Semi-Ready",     max:43,       color:"#1976d2", desc:"Good preparation; needs polishing." },
  { name:"Investor-Ready", max:49,       color:"#0E8F6E", desc:"Fully prepared; strong funding potential." },
  { name:"Exceptional",    max:Infinity, color:"#15824E", desc:"Exemplary readiness; a standout investment." },
];
const bandFor = (raw: number) => BANDS.find(b => raw < b.max) ?? BANDS[BANDS.length-1];

type QType = 'single' | 'multi';
interface Q { id: string; type: QType; q: string; hint?: string; opts: string[]; cond?: (s: Record<string,string>) => boolean }
const QUESTIONS: Q[] = [
  { id:"q1", type:"single", q:"What is your current runway?", hint:"How long you can operate on existing cash.", opts:Object.keys(RUNWAY) },
  { id:"q2", type:"single", q:"How much are you planning to raise this round?", hint:"USD. Combines with Q3 into one Raise-vs-cost score, and your raise also sets Q4 dilution.", opts:Object.keys(RAISE_MID) },
  { id:"q3", type:"single", q:"What is your total annual operating cost?", hint:"USD (annual burn). Pairs with Q2: one Raise-vs-cost score; it has no separate score of its own.", opts:Object.keys(COST_MID) },
  { id:"q4", type:"single", q:"What is your most recent valuation or SAFE cap?", hint:"USD. Used together with your raise (Q2) to score dilution.", opts:Object.keys(VAL_MID) },
  { id:"q5", type:"single", q:"When do you expect to reach profitability (breakeven)?", opts:Object.keys(Q5M) },
  { id:"q6", type:"single", q:"Main milestone you need to hit next?", opts:Object.keys(Q6) },
  { id:"q7", type:"single", q:"Does your startup currently have any debt?", opts:["No","Yes"] },
  { id:"q8", type:"single", q:"What kind of debt do you have?", cond:s=>s.q7==="Yes", opts:Object.keys(Q8) },
  { id:"q9", type:"single", q:"How stable is your revenue today?", opts:Object.keys(Q9) },
  { id:"q10", type:"single", q:"How would you respond to a major unexpected setback?", opts:Object.keys(Q10) },
  { id:"q11", type:"single", q:"How much experience do you have founding or leading startups?", opts:Object.keys(Q11) },
  { id:"q12", type:"multi", q:"Your founding team's key strengths?", hint:"Pick all that apply (optional).", cond:s=>s.q11!=="I don't have any experience", opts:["Successful prior startup exit","Leadership at a fast-growing company","Deep technical expertise","Strong industry network"] },
  { id:"q13", type:"single", q:"How do you make major strategic decisions?", opts:Object.keys(Q13) },
  { id:"q14", type:"single", q:"How do you view taking risk to achieve your goals?", opts:Object.keys(Q14) },
];
const BD: [string, string, number][] = [
  ["q1","Runway",5],["q2","Raise vs cost",5],["q4","Dilution",5],["q5","Profitability",5],
  ["q6","Milestone",5],["q7","Debt",10],["q8","Debt type",5],["q9","Revenue",5],
  ["q10","Setback",5],["q11","Experience",5],["q12","Strengths",4],["q13","Decisions",4],["q14","Risk appetite",5],
];
const CORE = ["q1","q2","q3","q4","q5","q6","q7","q9","q10","q11","q13","q14"]; // 12 required

function computeScore(answers: Record<string,string>, q12: string[]) {
  const s = answers; const p: Record<string, number> = {};
  p.q1 = RUNWAY[s.q1] ?? 0;
  const raise = RAISE_MID[s.q2] ?? 0, cost = COST_MID[s.q3] ?? 0, val = VAL_MID[s.q4] ?? 0;
  p.q2 = (raise>0 && cost>0) ? ratioPts(raise/cost) : 0;
  const d = (raise>0 && val>0) ? dilPts(raise/val) : { p:0, flag:false }; p.q4 = d.p;
  p.q5 = (s.q5 && s.q1) ? Q5M[s.q5][RUNCOL[s.q1]] : 0;
  p.q6 = Q6[s.q6] ?? 0; p.q7 = s.q7==="No" ? 10 : 0; p.q8 = (s.q7==="Yes") ? (Q8[s.q8] ?? 0) : 0;
  p.q9 = Q9[s.q9] ?? 0; p.q10 = Q10[s.q10] ?? 0; p.q11 = Q11[s.q11] ?? 0;
  p.q12 = (s.q11==="I don't have any experience") ? 0 : (q12 ? q12.length : 0);
  p.q13 = Q13[s.q13] ?? 0; p.q14 = Q14[s.q14] ?? 0;
  const raw = p.q1+p.q2+p.q4+p.q5+p.q6+p.q7+p.q8+p.q9+p.q10+p.q11+p.q12+p.q13+p.q14;
  return { p, raw, score: raw*MULT, dilFlag: d.flag };
}
function answeredCount(s: Record<string,string>) { let n=0; CORE.forEach(k=>{ if(s[k]) n++; }); if(s.q7==="Yes"&&s.q8) n++; return n; }
function totalQuestions(s: Record<string,string>) { return CORE.length + (s.q7==="Yes" ? 1 : 0); }

function detectCurrency(): 'AED' | 'USD' {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (/Asia\/(Dubai|Muscat|Qatar|Riyadh|Bahrain|Kuwait)/i.test(tz)) return 'AED';
  } catch { /* */ }
  return 'USD';
}

const Check = ({ color = '#15824E' }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.6" style={{ flexShrink:0, marginTop:2 }}><path d="M5 13l4 4L19 7" /></svg>
);

export default function CapitalReadinessAssessment() {
  const PRICES = useReportPricing();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [started, setStarted]   = useState(false);
  const [answers, setAnswers]   = useState<Record<string,string>>({});
  const [q12, setQ12]           = useState<string[]>([]);
  const [currency, setCurrency] = useState<'AED'|'USD'>('USD');
  const [unlocked, setUnlocked] = useState(false);

  const [saving, setSaving]         = useState(false);
  const [resultLocked, setResultLocked] = useState(false);
  const [lockedScore, setLockedScore]   = useState<number | null>(null);
  const [lockedBand,  setLockedBand]     = useState<string>('');
  const [lockedBreakdown, setLockedBreakdown] = useState<Record<string, number> | null>(null);
  const [lockedAnswers,   setLockedAnswers]   = useState<Record<string, string> | null>(null);
  const [lockedDilFlag,   setLockedDilFlag]   = useState(false);

  // Animation
  const [dispScore, setDispScore] = useState(0);
  const celebRef    = useRef(false);
  const submittedRef = useRef(false);
  const animRef     = useRef<number>(0);
  const canvasRef   = useRef<HTMLCanvasElement>(null);

  const { p, raw, score, dilFlag } = computeScore(answers, q12);
  const answered = answeredCount(answers);
  const total    = totalQuestions(answers);
  const complete = answered >= total && answered > 0;
  const band     = bandFor(raw);
  const pct      = Math.max(0, Math.min(100, score));

  // visible question numbering (skip hidden conditional)
  const visNums: Record<string, number> = {};
  { let n = 0; QUESTIONS.forEach(Q => { if (!Q.cond || Q.cond(answers)) { n++; visNums[Q.id] = n; } }); }
  const reqIds  = [...CORE, ...(answers.q7 === 'Yes' ? ['q8'] : [])];
  const missNums = reqIds.filter(k => !answers[k]).map(k => visNums[k]).filter(Boolean).sort((a,b)=>a-b);

  useEffect(() => { setCurrency(detectCurrency()); }, []);

  // On-mount: "take once" — if already submitted, show locked result
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (!userStr) return;
    const user = JSON.parse(userStr);
    const tokensStr = localStorage.getItem('authTokens');
    const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
    const controller = new AbortController();
    fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
      signal: controller.signal,
      headers: { ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
    })
      .then(r => r.json())
      .then(json => {
        const cr = json?.user?.capitalReadiness;
        if (cr && (cr.score !== undefined && cr.score !== null)) {
          setLockedScore(typeof cr.score === 'number' ? cr.score : parseFloat(cr.score));
          setLockedBand(cr.band || '');
          if (cr.breakdown && typeof cr.breakdown === 'object') setLockedBreakdown(cr.breakdown);
          if (cr.answers && typeof cr.answers === 'object') setLockedAnswers(cr.answers);
          setLockedDilFlag(!!cr.dilFlag);
          setResultLocked(true);
          setStarted(true);
        }
        // Restore the paid unlock so a returning buyer sees their breakdown revealed.
        if (json?.user?.capitalReportUnlocked) setUnlocked(true);
      })
      .catch(e => { if (e?.name !== 'AbortError') console.error(e); });
    return () => controller.abort();
  }, []);

  // Score reveal animation + auto-save when complete
  useEffect(() => {
    if (complete && !celebRef.current) {
      celebRef.current = true;
      const target = parseFloat(score.toFixed(1));
      const dur = 950, t0 = performance.now();
      const tick = (t: number) => {
        const k = Math.min(1, (t - t0) / dur);
        const e = 1 - Math.pow(1 - k, 3);
        setDispScore(parseFloat((target * e).toFixed(1)));
        if (k < 1) animRef.current = requestAnimationFrame(tick);
        else setDispScore(target);
      };
      animRef.current = requestAnimationFrame(tick);
      if (band.name === 'Investor-Ready' || band.name === 'Exceptional') fireConfetti();
      autoSave();
    }
    if (!complete) { celebRef.current = false; cancelAnimationFrame(animRef.current); }
  }, [complete]); // eslint-disable-line react-hooks/exhaustive-deps

  function autoSave() {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSaving(true);
    const tokensStr = localStorage.getItem('authTokens');
    const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
    if (!idToken) { setSaving(false); return; }
    fetch(`${API_BASE_URL}/auth/capital-readiness/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ answers, q12 }),
    })
      .then(r => r.json())
      .then(() => { setLockedScore(parseFloat(score.toFixed(1))); setLockedBand(band.name); })
      .catch(err => console.error('Capital readiness save error:', err))
      .finally(() => setSaving(false));
  }

  // Demo purchase (Stripe later). Unlocks the breakdown + the downloadable report,
  // and persists the flag so the dashboard shows "Your report is ready".
  function purchaseReport() {
    setUnlocked(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tokensStr = localStorage.getItem('authTokens');
      const idToken = tokensStr ? JSON.parse(tokensStr).idToken : null;
      if (user?.uid) {
        fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}) },
          body: JSON.stringify({ capitalReportUnlocked: true }),
        }).catch(() => {});
        try { localStorage.setItem('user', JSON.stringify({ ...user, capitalReportUnlocked: true })); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
  }

  function selectSingle(qid: string, val: string) {
    if (resultLocked) return;
    setAnswers(prev => ({ ...prev, [qid]: val }));
  }
  function toggleMulti(val: string) {
    if (resultLocked) return;
    setQ12(prev => prev.includes(val) ? prev.filter(v => v!==val) : [...prev, val]);
  }

  function fireConfetti() {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    c.style.display = 'block';
    const W = c.width = window.innerWidth, H = c.height = window.innerHeight;
    const cols = ['#1976d2','#5B9BD5','#FFD54F','#15824E','#0E8F6E','#ffffff'];
    const parts = Array.from({ length: 130 }, (_, i) => ({
      x: W/2+(Math.random()-.5)*220, y: H*.3+(Math.random()-.5)*40,
      vx: (Math.random()-.5)*9, vy: Math.random()*-9-3, g: .22+Math.random()*.1,
      s: 5+Math.random()*6, rot: Math.random()*6, vr: (Math.random()-.5)*.4,
      col: cols[i%cols.length], life: 1,
    }));
    let frame = 0;
    const draw = () => {
      ctx.clearRect(0,0,W,H); frame++; let alive = false;
      parts.forEach(pt => {
        pt.vy += pt.g; pt.x += pt.vx; pt.y += pt.vy; pt.rot += pt.vr; pt.life -= .008;
        if (pt.life > 0 && pt.y < H+20) {
          alive = true; ctx.save(); ctx.globalAlpha = Math.max(0, pt.life);
          ctx.translate(pt.x, pt.y); ctx.rotate(pt.rot); ctx.fillStyle = pt.col;
          ctx.fillRect(-pt.s/2, -pt.s/2, pt.s, pt.s*.6); ctx.restore();
        }
      });
      if (alive && frame < 260) requestAnimationFrame(draw);
      else { ctx.clearRect(0,0,W,H); c.style.display = 'none'; }
    };
    requestAnimationFrame(draw);
  }

  const arcOffset = 264 - 264 * (complete ? pct : 0) / 100;
  const shownScore = resultLocked && lockedScore != null ? lockedScore : dispScore;
  const shownBand  = resultLocked && lockedBand ? (BANDS.find(b => b.name === lockedBand) ?? band) : band;

  // Factor rows to show in the locked (already-completed) view. Prefer the saved
  // answers to decide which conditional factors apply; fall back to the breakdown
  // values when this is an older submission that didn't persist the answers.
  const lockedBd = BD.filter(([k]) => {
    if (k === 'q8')  return lockedAnswers ? lockedAnswers.q7 === 'Yes' : (lockedBreakdown?.[k] ?? 0) > 0;
    if (k === 'q12') return lockedAnswers ? lockedAnswers.q11 !== "I don't have any experience" : true;
    return true;
  });

  // Shared report card — used by both the in-progress completion panel and the
  // full-width already-completed view. Shows the paywall (buy tiers) until the
  // user unlocks (demo purchase), then a "report ready" confirmation.
  const renderUnlockCard = () => (
    <div style={{ background:WHITE, border:`1px solid ${LINE}`, borderRadius:18, overflow:'hidden' }}>
      {unlocked ? (
        <>
          <div style={{ background:'linear-gradient(120deg,#0E8F6E,#15824E)', color:WHITE, padding:'20px 24px' }}>
            <div style={{ fontSize:11, letterSpacing:'.08em', textTransform:'uppercase', color:'#CFF3E6', fontWeight:800 }}>Unlocked</div>
            <h3 style={{ fontSize:19, color:WHITE, marginTop:4, fontWeight:800, display:'flex', alignItems:'center', gap:8 }}>
              <CheckCircle2 className="w-5 h-5" /> Your report is ready 🎉
            </h3>
            <p style={{ color:'#DDF4EA', fontSize:13, marginTop:4 }}>Your full factor-by-factor breakdown is revealed below. Download the detailed report any time from your dashboard.</p>
          </div>
          <div style={{ padding:'18px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
            <div style={{ fontSize:12.5, color:MUTED, fontWeight:600 }}>Saved to your dashboard.</div>
            <button onClick={() => navigate('/dashboard')}
              style={{ border:'none', background:BRAND, color:WHITE, fontFamily:'inherit', fontWeight:800, fontSize:14, padding:'11px 22px', borderRadius:999, cursor:'pointer' }}>
              Check your report →
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={{ background:'linear-gradient(120deg,#10243b,#1b3550)', color:WHITE, padding:'20px 24px' }}>
            <div style={{ fontSize:11, letterSpacing:'.08em', textTransform:'uppercase', color:'#9fb0c9', fontWeight:800 }}>Unlock</div>
            <h3 style={{ fontSize:19, color:WHITE, marginTop:4, fontWeight:800 }}>Get your full report</h3>
            <p style={{ color:'#b9c4d2', fontSize:13, marginTop:4 }}>Unlock your factor-by-factor breakdown, peer benchmark and a prioritised action plan to strengthen your raise.</p>
            <div style={{ display:'inline-flex', border:'1px solid rgba(255,255,255,.25)', borderRadius:9, overflow:'hidden', marginTop:12 }}>
              {(['AED','USD'] as const).map(cur => (
                <button key={cur} onClick={() => setCurrency(cur)}
                  style={{ border:'none', background: currency===cur ? WHITE : 'transparent', color: currency===cur ? INK : '#9fb0c9', fontFamily:'inherit', fontWeight:800, fontSize:12, padding:'7px 15px', cursor:'pointer' }}>
                  {cur==='AED' ? <><Dh em="0.8"/>AED</> : 'USD'}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, padding:'20px 24px' }} className="cr-tiers">
            <div style={{ border:`1.5px solid ${LINE}`, borderRadius:14, padding:18, display:'flex', flexDirection:'column' }}>
              <h4 style={{ fontSize:15, fontWeight:800, color:INK }}>Report</h4>
              <div style={{ margin:'8px 0 2px' }}><b style={{ fontSize:26, fontWeight:900, color:INK }}>{currency==='AED' ? <><Dh em="0.7"/></> : '$'}{PRICES[currency].p1}</b></div>
              <div style={{ fontSize:11, color:MUTED, fontWeight:700, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:10 }}>one-time</div>
              <ul style={{ listStyle:'none', margin:'0 0 16px', display:'flex', flexDirection:'column', gap:7, padding:0 }}>
                {['Full 14-factor breakdown','Peer benchmark by stage','Prioritised action plan (PDF)'].map(f => (
                  <li key={f} style={{ display:'flex', gap:8, fontSize:12.5, color:INK2, alignItems:'flex-start' }}><Check />{f}</li>
                ))}
              </ul>
              <button onClick={purchaseReport}
                style={{ marginTop:'auto', border:'none', borderRadius:10, fontFamily:'inherit', fontWeight:800, fontSize:13.5, padding:11, cursor:'pointer', background:INK, color:WHITE }}>
                Unlock report
              </button>
            </div>
            <div style={{ border:`1.5px solid ${BRAND}`, borderRadius:14, padding:18, display:'flex', flexDirection:'column', position:'relative', boxShadow:'0 0 0 2px rgba(25,118,210,.12)' }}>
              <div style={{ position:'absolute', top:-11, left:'50%', transform:'translateX(-50%)', background:BRAND, color:WHITE, fontSize:10.5, fontWeight:800, padding:'3px 12px', borderRadius:999, whiteSpace:'nowrap' }}>Best value</div>
              <h4 style={{ fontSize:15, fontWeight:800, color:INK }}>Report + advisory</h4>
              <div style={{ margin:'8px 0 2px' }}><b style={{ fontSize:26, fontWeight:900, color:INK }}>{currency==='AED' ? <><Dh em="0.7"/></> : '$'}{PRICES[currency].p2}</b></div>
              <div style={{ fontSize:11, color:MUTED, fontWeight:700, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:10 }}>one-time</div>
              <ul style={{ listStyle:'none', margin:'0 0 16px', display:'flex', flexDirection:'column', gap:7, padding:0 }}>
                {['Everything in Report','1-hour advisory session','Tailored raise game-plan'].map(f => (
                  <li key={f} style={{ display:'flex', gap:8, fontSize:12.5, color:INK2, alignItems:'flex-start' }}><Check />{f}</li>
                ))}
              </ul>
              <button onClick={purchaseReport}
                style={{ marginTop:'auto', border:'none', borderRadius:10, fontFamily:'inherit', fontWeight:800, fontSize:13.5, padding:11, cursor:'pointer', background:BRAND, color:WHITE }}>
                Unlock + book session
              </button>
            </div>
          </div>
          {/* Continue */}
          <div style={{ borderTop:`1px solid ${LINE}`, padding:'16px 24px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap' }}>
            <div style={{ fontSize:12.5, color:MUTED, fontWeight:600 }}>
              {saving ? 'Saving your score…' : 'Your score is saved to your dashboard.'}
            </div>
            <button onClick={() => navigate('/dashboard')}
              style={{ border:'none', background:'transparent', color:MUTED, fontFamily:'inherit', fontWeight:700, fontSize:13, cursor:'pointer', textDecoration:'underline' }}>
              Skip for now
            </button>
          </div>
        </>
      )}
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <canvas ref={canvasRef} style={{ position:'fixed', inset:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:9999, display:'none' }} />
      <div className="flex h-screen overflow-hidden" style={{ background:BG }}>
        <Sidebar />
        <main className="flex-1 md:ms-64 overflow-y-auto relative">
          <MobileNav onLogout={logout} />
          <div className="absolute top-6 end-8 z-10"><UserProfileMenu /></div>

          {/* Header */}
          <header style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16, padding:'20px 32px', borderBottom:`1px solid ${LINE}`, background:WHITE }}>
            <div>
              <h1 style={{ fontWeight:800, fontSize:24, letterSpacing:'-.02em', color:INK }}>Capital Readiness</h1>
              <div style={{ color:MUTED, fontSize:13.5, fontWeight:500, marginTop:2 }}>Answer 14 questions for your Capital Readiness Score, then unlock the full report.</div>
            </div>
          </header>

          <div style={{ padding:'28px 32px 64px', maxWidth:1040, margin:'0 auto' }}>

            {/* INTRO */}
            {!started && (
              <div style={{ display:'grid', gridTemplateColumns:'1.1fr .9fr', gap:0, background:WHITE, border:`1px solid ${LINE}`, borderRadius:22, boxShadow:'0 26px 60px -24px rgba(23,38,58,.30)', overflow:'hidden' }} className="cr-intro">
                <div style={{ padding:'36px 38px' }}>
                  <span style={{ fontWeight:800, fontSize:11, letterSpacing:'.14em', textTransform:'uppercase', color:BRAND, background:ICE, display:'inline-flex', padding:'6px 14px', borderRadius:999 }}>Capital Readiness</span>
                  <h2 style={{ fontSize:30, margin:'14px 0 10px', letterSpacing:'-.03em', color:INK, fontWeight:800 }}>See how investor-ready you really are</h2>
                  <p style={{ color:INK2, fontSize:15.5, lineHeight:1.55 }}>Answer a short set of questions and get your <b>Capital Readiness Score</b> instantly, across the 14 factors investors actually weigh. Then unlock a full report with a prioritised plan to strengthen your raise.</p>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', margin:'16px 0 4px' }}>
                    {['12 core questions','~4 minutes','Instant score'].map(f => (
                      <span key={f} style={{ background:'#F4F6F9', border:`1px solid ${LINE}`, borderRadius:999, padding:'6px 13px', fontSize:12, fontWeight:700, color:INK2 }}>{f}</span>
                    ))}
                  </div>
                  <ul style={{ listStyle:'none', margin:'14px 0 22px', display:'flex', flexDirection:'column', gap:9, padding:0 }}>
                    {['A clear 0–100 score and readiness band','A factor-by-factor breakdown of strengths and gaps','An action plan to improve before you raise'].map(f => (
                      <li key={f} style={{ display:'flex', gap:9, fontSize:14, color:INK2, alignItems:'flex-start' }}><Check />{f}</li>
                    ))}
                  </ul>
                  <button onClick={() => setStarted(true)}
                    style={{ border:'none', background:BRAND, color:WHITE, fontFamily:'inherit', fontWeight:800, fontSize:16, padding:'14px 28px', borderRadius:999, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:9 }}>
                    Start assessment <span>→</span>
                  </button>
                  <div style={{ fontSize:12, color:MUTED, fontWeight:600, marginTop:12 }}>Your score is free. The full report is a one-time unlock.</div>
                  <div style={{ marginTop:14 }}>
                    <button onClick={() => navigate('/dashboard')}
                      style={{ border:'none', background:'transparent', color:MUTED, fontFamily:'inherit', fontWeight:700, fontSize:13, cursor:'pointer', textDecoration:'underline' }}>
                      Skip for now
                    </button>
                  </div>
                </div>
                <div style={{ background:'linear-gradient(160deg,#10243b,#1b3550)', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', padding:34 }} className="cr-intro-side">
                  <div style={{ position:'relative', width:200, height:120 }}>
                    <svg viewBox="0 0 200 120" style={{ width:'100%', height:'100%', overflow:'visible' }}>
                      <path d="M16 110 A 84 84 0 0 1 184 110" fill="none" stroke="#22324c" strokeWidth="16" strokeLinecap="round"/>
                      <path d="M16 110 A 84 84 0 0 1 184 110" fill="none" stroke={BRAND_BRT} strokeWidth="16" strokeLinecap="round" strokeDasharray="264" strokeDashoffset="92"/>
                    </svg>
                    <div style={{ position:'absolute', left:0, right:0, top:52, textAlign:'center' }}>
                      <b style={{ fontSize:28, fontWeight:900, color:WHITE, display:'block', lineHeight:1 }}>0–100</b>
                      <span style={{ fontSize:10, color:'#9fb0c9', fontWeight:700 }}>Capital Readiness</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', width:200, height:8, borderRadius:999, overflow:'hidden', marginTop:18 }}>
                    {BANDS.map(b => <span key={b.name} style={{ flex:1, background:b.color }} />)}
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', width:200, marginTop:6, fontSize:10.5, color:'#9fb0c9', fontWeight:700 }}>
                    <span>Critical</span><span>Exceptional</span>
                  </div>
                </div>
              </div>
            )}

            {/* ASSESSMENT (in progress / just completed) */}
            {started && !resultLocked && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 360px', gap:24, alignItems:'start' }} className="cr-assess">
                {/* Question list */}
                <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
                  {QUESTIONS.map(Q => {
                    if (Q.cond && !Q.cond(answers)) return null;
                    const num = visNums[Q.id];
                    const isAnswered = Q.type === 'multi' ? q12.length > 0 : !!answers[Q.id];
                    return (
                      <div key={Q.id} style={{ background:WHITE, border:`1px solid ${isAnswered ? '#CFE3F7' : LINE}`, borderRadius:16, padding:'20px 22px' }}>
                        <div style={{ display:'flex', gap:11, alignItems:'flex-start', marginBottom:13 }}>
                          <span style={{ flexShrink:0, width:26, height:26, borderRadius:8, background: isAnswered ? BRAND : INK, color:WHITE, fontWeight:800, fontSize:12.5, display:'flex', alignItems:'center', justifyContent:'center' }}>{num}</span>
                          <div>
                            <div style={{ fontWeight:800, fontSize:15.5, lineHeight:1.3, color:INK }}>{Q.q}</div>
                            {Q.hint && <div style={{ fontSize:12, color:MUTED, fontWeight:600, marginTop:2 }}>{Q.hint}</div>}
                          </div>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', gap:7 }}>
                          {Q.opts.map(opt => {
                            const sel = Q.type === 'multi' ? q12.includes(opt) : answers[Q.id] === opt;
                            return (
                              <div key={opt} onClick={() => Q.type === 'multi' ? toggleMulti(opt) : selectSingle(Q.id, opt)}
                                style={{ display:'flex', alignItems:'center', gap:10, border:`1.5px solid ${sel ? BRAND : LINE}`, borderRadius:10, padding:'10px 13px', fontSize:13.5, fontWeight:600, cursor:'pointer', color: sel ? INK : INK2, background: sel ? ICE : 'transparent', transition:'border-color .15s, background .15s' }}>
                                <span style={{ width:17, height:17, borderRadius: Q.type === 'multi' ? 5 : '50%', border:`2px solid ${sel ? BRAND : '#CBD2DA'}`, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                  {sel && (Q.type === 'multi'
                                    ? <span style={{ color:BRAND, fontSize:11, fontWeight:900, lineHeight:1 }}>✓</span>
                                    : <span style={{ width:8, height:8, borderRadius:'50%', background:BRAND, display:'block' }} />)}
                                </span>
                                {opt}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Sticky results panel */}
                <aside style={{ position:'sticky', top:24, alignSelf:'start' }} className="cr-panel">
                  <div style={{ background:INK, color:WHITE, borderRadius:20, boxShadow:'0 26px 60px -24px rgba(23,38,58,.30)', overflow:'hidden', padding:'22px 24px 18px' }}>
                    <div style={{ fontSize:11, letterSpacing:'.1em', textTransform:'uppercase', color:'#9fb0c9', fontWeight:800, textAlign:'center' }}>Capital Readiness Score</div>
                    <div style={{ position:'relative', width:190, height:114, margin:'8px auto 0' }}>
                      <svg viewBox="0 0 200 120" style={{ width:'100%', height:'100%', overflow:'visible' }}>
                        <path d="M16 110 A 84 84 0 0 1 184 110" fill="none" stroke="#22324c" strokeWidth="16" strokeLinecap="round"/>
                        <path d="M16 110 A 84 84 0 0 1 184 110" fill="none"
                          stroke={(complete || resultLocked) ? shownBand.color : '#22324c'} strokeWidth="16" strokeLinecap="round"
                          strokeDasharray="264" strokeDashoffset={resultLocked && lockedScore != null ? 264 - 264*Math.min(100,lockedScore)/100 : arcOffset}
                          style={{ transition:'stroke-dashoffset .4s, stroke .4s' }} />
                      </svg>
                      <div style={{ position:'absolute', left:0, right:0, top:50, textAlign:'center' }}>
                        <b style={{ fontSize:40, fontWeight:900, letterSpacing:'-.03em', display:'block', lineHeight:1 }}>{(complete || resultLocked) ? shownScore.toFixed(1) : '-'}</b>
                        <span style={{ fontSize:11, color:'#9fb0c9', fontWeight:700 }}>of 100</span>
                      </div>
                    </div>
                    <div style={{ textAlign:'center' }}>
                      <span style={{ display:'inline-block', fontWeight:800, fontSize:14, padding:'6px 16px', borderRadius:999, marginTop:6,
                        background: (complete || resultLocked) ? shownBand.color : '#22324c', color: (complete || resultLocked) ? '#fff' : '#9fb0c9' }}>
                        {(complete || resultLocked) ? shownBand.name : `Complete all ${total} questions`}
                      </span>
                      <div style={{ color:'#AEB9D6', fontSize:12.5, fontWeight:600, marginTop:8, minHeight:30, padding:'0 6px' }}>
                        {(complete || resultLocked) ? shownBand.desc : (missNums.length && missNums.length <= 3 ? `Answer Q${missNums.join(', Q')} to reveal your score.` : 'Your readiness score appears here once every question is answered.')}
                      </div>
                    </div>

                    {/* progress */}
                    <div style={{ margin:'12px 0 0', height:7, background:'#22324c', borderRadius:999, overflow:'hidden' }}>
                      <div style={{ height:'100%', background:`linear-gradient(90deg,${BRAND_BRT},${BRAND})`, width:`${resultLocked ? 100 : Math.min(100, answered/total*100)}%`, transition:'width .4s' }} />
                    </div>
                    <div style={{ color:'#9fb0c9', fontSize:11.5, fontWeight:700, textAlign:'center', marginTop:6 }}>
                      {resultLocked ? 'Complete' : `${answered} of ${total} answered${complete ? ' · complete' : ''}`}
                    </div>

                    {dilFlag && !resultLocked && (
                      <div style={{ background:'#3a2030', color:'#ffd0d8', border:'1px solid #5a2a3c', borderRadius:10, padding:'8px 12px', fontSize:11.5, fontWeight:700, marginTop:8, display:'flex', alignItems:'center', gap:7 }}>
                        <AlertTriangle className="w-4 h-4" style={{ flexShrink:0 }} /> Your raise exceeds your valuation (&gt;100% dilution), investors will flag this.
                      </div>
                    )}

                    {/* breakdown (locked until unlock) */}
                    {(complete || resultLocked) && (
                      <div style={{ paddingTop:14 }}>
                        <h4 style={{ fontSize:11, letterSpacing:'.08em', textTransform:'uppercase', color:'#9fb0c9', marginBottom:10, fontWeight:800 }}>Score breakdown</h4>
                        <div style={{ position:'relative' }}>
                          <div style={{ filter: unlocked ? 'none' : 'blur(5px)', opacity: unlocked ? 1 : .65, pointerEvents: unlocked ? 'auto' : 'none', userSelect:'none' }}>
                            {BD.filter(r =>
                              !(r[0]==='q8' && answers.q7!=='Yes') &&
                              !(r[0]==='q12' && answers.q11==="I don't have any experience")
                            ).map(([k, label, mx]) => {
                              const v = p[k] ?? 0; const w = Math.min(100, v/mx*100);
                              return (
                                <div key={k} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:7 }}>
                                  <span style={{ fontSize:11.5, color:'#cdd8ea', fontWeight:600, width:104, flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{label}</span>
                                  <span style={{ flex:1, height:6, background:'#22324c', borderRadius:999, overflow:'hidden' }}>
                                    <span style={{ display:'block', height:'100%', background:BRAND_BRT, width:`${w}%`, borderRadius:999 }} />
                                  </span>
                                  <span style={{ fontSize:11, color:'#fff', fontWeight:800, width:30, textAlign:'right' }}>{Math.round(v*10)/10}</span>
                                </div>
                              );
                            })}
                          </div>
                          {!unlocked && (
                            <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:7, textAlign:'center', padding:10, color:'#dbe4f0', fontSize:12, fontWeight:700 }}>
                              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#FFE38A" style={{ width:22, height:22 }}><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>
                              <div>Your full factor-by-factor breakdown</div>
                              <div style={{ color:'#FFE38A' }}>Unlock below to reveal ↓</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Unlock card */}
                  {complete && (
                    <div style={{ marginTop:20 }}>{renderUnlockCard()}</div>
                  )}

                  {/* Skip (while answering) */}
                  {!complete && !resultLocked && (
                    <div style={{ textAlign:'center', marginTop:16 }}>
                      <button onClick={() => navigate('/dashboard')}
                        style={{ border:'none', background:'transparent', color:MUTED, fontFamily:'inherit', fontWeight:700, fontSize:13, cursor:'pointer', textDecoration:'underline' }}>
                        Skip for now
                      </button>
                    </div>
                  )}
                </aside>
              </div>
            )}

            {/* ALREADY COMPLETED — full-width result (message on top, score + breakdown span the width) */}
            {started && resultLocked && (
              <div style={{ display:'flex', flexDirection:'column', gap:20 }} className="cr-locked">
                {/* Message — top, full width */}
                <div style={{ background:'#D7F0E3', border:'1px solid #A9DCC2', color:'#0F5C36', borderRadius:14, padding:'14px 18px', fontSize:14, fontWeight:700, display:'flex', alignItems:'center', gap:10 }}>
                  <Lock className="w-4 h-4" style={{ flexShrink:0 }} /> You've already completed this assessment. Your result is saved and locked.
                </div>

                {/* Score + breakdown — full width */}
                <div style={{ background:INK, color:WHITE, borderRadius:22, boxShadow:'0 26px 60px -24px rgba(23,38,58,.30)', overflow:'hidden', padding:'30px 34px' }}>
                  <div className="cr-locked-grid" style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:40, alignItems:'center' }}>
                    {/* Gauge + band */}
                    <div style={{ textAlign:'center' }}>
                      <div style={{ fontSize:11, letterSpacing:'.1em', textTransform:'uppercase', color:'#9fb0c9', fontWeight:800 }}>Capital Readiness Score</div>
                      <div style={{ position:'relative', width:210, height:126, margin:'10px auto 0' }}>
                        <svg viewBox="0 0 200 120" style={{ width:'100%', height:'100%', overflow:'visible' }}>
                          <path d="M16 110 A 84 84 0 0 1 184 110" fill="none" stroke="#22324c" strokeWidth="16" strokeLinecap="round"/>
                          <path d="M16 110 A 84 84 0 0 1 184 110" fill="none"
                            stroke={shownBand.color} strokeWidth="16" strokeLinecap="round"
                            strokeDasharray="264" strokeDashoffset={264 - 264 * Math.min(100, lockedScore ?? 0) / 100}
                            style={{ transition:'stroke-dashoffset .6s, stroke .6s' }} />
                        </svg>
                        <div style={{ position:'absolute', left:0, right:0, top:54, textAlign:'center' }}>
                          <b style={{ fontSize:44, fontWeight:900, letterSpacing:'-.03em', display:'block', lineHeight:1 }}>{(lockedScore ?? 0).toFixed(1)}</b>
                          <span style={{ fontSize:11, color:'#9fb0c9', fontWeight:700 }}>of 100</span>
                        </div>
                      </div>
                      <span style={{ display:'inline-block', fontWeight:800, fontSize:15, padding:'7px 18px', borderRadius:999, marginTop:8, background:shownBand.color, color:'#fff' }}>{shownBand.name}</span>
                      <div style={{ color:'#AEB9D6', fontSize:13, fontWeight:600, marginTop:10, lineHeight:1.5, maxWidth:240, marginLeft:'auto', marginRight:'auto' }}>{shownBand.desc}</div>
                    </div>

                    {/* Breakdown — gated behind the paid unlock (blurred until purchased) */}
                    <div>
                      <h4 style={{ fontSize:11.5, letterSpacing:'.08em', textTransform:'uppercase', color:'#9fb0c9', marginBottom:14, fontWeight:800 }}>Score breakdown: your factors</h4>
                      <div style={{ position:'relative' }}>
                        <div style={{ filter: unlocked ? 'none' : 'blur(6px)', opacity: unlocked ? 1 : .55, pointerEvents: unlocked ? 'auto' : 'none', userSelect:'none' }}>
                          {lockedBreakdown ? (
                            <div className="cr-bd-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', columnGap:30, rowGap:12 }}>
                              {lockedBd.map(([k, label, mx]) => {
                                const v = lockedBreakdown?.[k] ?? 0; const w = Math.min(100, v / mx * 100);
                                return (
                                  <div key={k} style={{ display:'flex', alignItems:'center', gap:10 }}>
                                    <span title={label} style={{ fontSize:12, color:'#cdd8ea', fontWeight:600, width:104, flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{label}</span>
                                    <span style={{ flex:1, height:7, background:'#22324c', borderRadius:999, overflow:'hidden' }}>
                                      <span style={{ display:'block', height:'100%', background:`linear-gradient(90deg,${BRAND_BRT},${BRAND})`, width:`${w}%`, borderRadius:999 }} />
                                    </span>
                                    <span style={{ fontSize:11.5, color:'#fff', fontWeight:800, width:42, textAlign:'right', flexShrink:0 }}>{Math.round(v*10)/10}<span style={{ color:'#7d8aa3', fontWeight:700 }}>/{mx}</span></span>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{ color:'#AEB9D6', fontSize:13, fontWeight:600, lineHeight:1.6 }}>
                              {unlocked
                                ? "The factor-by-factor breakdown wasn't saved for this earlier submission. Your overall score and band above are final."
                                : 'Your full factor-by-factor breakdown: runway, raise, dilution, revenue, team and more.'}
                            </div>
                          )}
                          {lockedDilFlag && unlocked && (
                            <div style={{ background:'#3a2030', color:'#ffd0d8', border:'1px solid #5a2a3c', borderRadius:10, padding:'9px 13px', fontSize:12, fontWeight:700, marginTop:18, display:'inline-flex', alignItems:'center', gap:7 }}>
                              <AlertTriangle className="w-4 h-4" style={{ flexShrink:0 }} /> Your raise exceeds your valuation (&gt;100% dilution), investors will flag this.
                            </div>
                          )}
                        </div>
                        {!unlocked && (
                          <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:8, textAlign:'center', padding:12 }}>
                            <div style={{ width:42, height:42, borderRadius:'50%', background:'rgba(255,227,138,.14)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                              <Lock className="w-5 h-5" style={{ color:'#FFE38A' }} />
                            </div>
                            <div style={{ color:'#fff', fontSize:13.5, fontWeight:800 }}>Your factor-by-factor breakdown is locked</div>
                            <div style={{ color:'#FFE38A', fontSize:12.5, fontWeight:700 }}>Unlock your report below to reveal ↓</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Unlock full report — full width */}
                {renderUnlockCard()}
              </div>
            )}
          </div>
        </main>
      </div>

      <style>{`
        @media(max-width:980px){
          .cr-assess { grid-template-columns: 1fr !important; }
          .cr-panel  { position: static !important; }
        }
        @media(max-width:760px){
          .cr-intro { grid-template-columns: 1fr !important; }
          .cr-intro-side { display: none !important; }
          .cr-tiers { grid-template-columns: 1fr !important; }
          .cr-locked-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .cr-bd-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}
